/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {WelcomePage} from '../modules/onboarding/WelcomePage'
import {PublicRoutes} from './PublicRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import TrelloCallback from '../modules/integrations/components/TrelloCallback'
import {ImpersonateCallback} from '../modules/auth/components/ImpersonateCallback'
import {ClientPortalRoutes} from './ClientPortalRoutes'
import {ClientPortalAuthProvider} from '../modules/auth/core/ClientPortalAuth'
import {ClientPortalUserRegistration} from '../modules/auth/components/ClientPortalUserRegistration'
import {ClientPortalLogin} from '../modules/auth/components/ClientPortalLogin'
import {ClientPortalForgotPassword} from '../modules/auth/components/ClientPortalForgotPassword'
import {ClientPortalResetPassword} from '../modules/auth/components/ClientPortalResetPassword'
import {PostLoginRedirect, SaveConnectAndLogin} from './ConnectRedirects'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        {/* Impersonate callback - MUST be completely outside App wrapper */}
        <Route
          path="/auth/impersonate"
          element={<ImpersonateCallback />}
        />
        
        <Route element={<App />}>
          {/* Public routes that must come first to avoid auth guards */}
          <Route path='public/*' element={<PublicRoutes />} />
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          
          {/* Trello callback must be public to avoid guard interception */}
          <Route
            path="/admin/integrations/trello-callback"
            element={<TrelloCallback />}
          />
          
          {/* Rotas do portal do cliente - SEMPRE acessíveis, independente do login do sistema principal */}
          {/* Renderizar diretamente os componentes, sem passar pelo AuthPage */}
          <Route path='auth/area-do-cliente/cadastro' element={<ClientPortalUserRegistration />} />
          <Route path='auth/area-do-cliente/login' element={<ClientPortalLogin />} />
          <Route path='auth/area-do-cliente/forgot-password' element={<ClientPortalForgotPassword />} />
          <Route path='auth/area-do-cliente/reset-password' element={<ClientPortalResetPassword />} />
          <Route path='area-do-cliente/*' element={
            <ClientPortalAuthProvider>
              <ClientPortalRoutes />
            </ClientPortalAuthProvider>
          } />
          
          {currentUser ? (
            <>
              {!currentUser.onboarded ? (
                <>
                  <Route path='onboarding/*' element={<WelcomePage />} />
                  {/* Permitir redirecionamento de /auth/* para /onboarding após login */}
                  <Route path='auth/*' element={<Navigate to='/onboarding' replace />} />
                  <Route path='*' element={<Navigate to='/onboarding' />} />
                </>
              ) : (
                <>
                  {/* Rotas privadas do sistema principal */}
                  {/* Após o login, retoma o link de vínculo do RushMind (se veio de um) ou vai ao dashboard */}
                  <Route path='auth/*' element={<PostLoginRedirect />} />
                  <Route path='/*' element={<PrivateRoutes />} />
                  <Route index element={<Navigate to='/dashboard' />} />
                </>
              )}
            </>
          ) : (
            <>
              {/* AuthPage handles all other /auth/* routes (login, register, etc) */}
              <Route path='auth/*' element={<AuthPage />} />
              {/* Link de vínculo do RushMind aberto sem login: guarda o destino e vai ao login */}
              <Route path='connect/*' element={<SaveConnectAndLogin />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
